import React, { useState } from 'react';
import { Smartphone, Download, Check, Sparkles, QrCode, Star, Heart } from 'lucide-react';
import { PairUpLogo } from './common/PairUpLogo';

interface DownloadAppSectionProps {
  onShowQRModal?: () => void;
}

export const DownloadAppSection: React.FC<DownloadAppSectionProps> = ({ onShowQRModal }) => {
  const [copiedLink, setCopiedLink] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [smsSent, setSmsSent] = useState(false);

  const handleSendSMS = (e: React.FormEvent) => {
    e.preventDefault();
    if (phoneNumber.length >= 10) {
      setSmsSent(true);
      setTimeout(() => setSmsSent(false), 4000);
    }
  };

  return (
    <section id="download-app-section" className="py-12 sm:py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Main Purple Gradient Banner matching screenshot */}
        <div className="relative rounded-3xl overflow-hidden bg-gradient-to-r from-purple-900 via-indigo-900 to-purple-800 text-white p-8 sm:p-12 lg:p-16 shadow-2xl">
          
          {/* Floating background heart ambient effects */}
          <div className="absolute top-8 left-1/3 text-pink-400/20 text-5xl animate-float-slow pointer-events-none select-none">
            💕
          </div>
          <div className="absolute bottom-6 right-1/4 text-purple-400/20 text-6xl animate-float-gentle pointer-events-none select-none">
            💗
          </div>
          <div className="absolute top-1/2 right-12 text-rose-300/15 text-4xl animate-float-slow pointer-events-none select-none">
            💖
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center relative z-10">
            
            {/* Left Info Column */}
            <div className="lg:col-span-7 space-y-6">
              <div className="space-y-3">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 backdrop-blur-md text-pink-300 text-xs font-semibold">
                  <Sparkles className="w-3.5 h-3.5" />
                  <span>Available on iOS & Android</span>
                </div>
                
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight">
                  Download Our App
                </h2>
                
                <p className="text-pink-100/90 text-base sm:text-lg max-w-md font-normal">
                  Better experience on mobile. Book companions in real-time, get instant updates, and chat securely.
                </p>
              </div>

              {/* App Store & Google Play Badges matching screenshot */}
              <div className="flex flex-wrap gap-4 pt-2">
                
                {/* App Store Button */}
                <button
                  id="btn-download-app-store"
                  onClick={() => onShowQRModal ? onShowQRModal() : alert('Redirecting to iOS App Store (PairUpToday)...')}
                  className="flex items-center gap-3 px-5 py-3 rounded-2xl bg-black/80 hover:bg-black border border-white/20 hover:border-white/40 transition-all shadow-lg hover:scale-105 cursor-pointer text-left"
                >
                  <svg className="w-7 h-7 fill-white" viewBox="0 0 24 24">
                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M15.97 6.37c.62-.75 1.04-1.8 0.92-2.85-.9.04-1.99.6-2.63 1.35-.57.65-1.07 1.72-.94 2.74 1 .08 2.03-.49 2.65-1.24z"/>
                  </svg>
                  <div>
                    <p className="text-[10px] uppercase font-medium text-slate-300 leading-none">Download on the</p>
                    <p className="text-sm font-bold text-white leading-tight">App Store</p>
                  </div>
                </button>

                {/* Google Play Button */}
                <button
                  id="btn-download-google-play"
                  onClick={() => onShowQRModal ? onShowQRModal() : alert('Redirecting to Google Play Store (PairUpToday)...')}
                  className="flex items-center gap-3 px-5 py-3 rounded-2xl bg-black/80 hover:bg-black border border-white/20 hover:border-white/40 transition-all shadow-lg hover:scale-105 cursor-pointer text-left"
                >
                  <svg className="w-7 h-7" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M3.609 1.814L13.792 12 3.61 22.186c-.365-.357-.61-.884-.61-1.488V3.302c0-.604.245-1.131.609-1.488z"/>
                    <path fill="#FBBC04" d="M17.156 8.636l-3.364 3.364 3.364 3.364 3.834-2.184c.677-.386.677-1.958 0-2.344l-3.834-2.2z"/>
                    <path fill="#EA4335" d="M3.609 1.814l10.183 10.186 3.364-3.364L4.78.36c-.463-.264-.993-.207-1.171 1.454z"/>
                    <path fill="#34A853" d="M13.792 12L3.609 22.186c.178 1.661.708 1.718 1.171 1.454l12.376-7.076-3.364-3.564z"/>
                  </svg>
                  <div>
                    <p className="text-[10px] uppercase font-medium text-slate-300 leading-none">GET IT ON</p>
                    <p className="text-sm font-bold text-white leading-tight">Google Play</p>
                  </div>
                </button>

              </div>

              {/* Quick SMS link input */}
              <div className="pt-2">
                <form onSubmit={handleSendSMS} className="flex max-w-sm gap-2">
                  <input
                    type="tel"
                    placeholder="Enter mobile number for link"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    className="px-4 py-2.5 rounded-xl bg-white/10 border border-white/20 text-white placeholder-pink-200/50 text-xs focus:outline-none focus:ring-2 focus:ring-pink-400 flex-1"
                  />
                  <button
                    type="submit"
                    className="px-4 py-2.5 bg-pink-500 hover:bg-pink-600 font-bold text-xs rounded-xl text-white transition-colors shrink-0"
                  >
                    {smsSent ? 'Sent! ✓' : 'Send Link'}
                  </button>
                </form>
              </div>

            </div>

            {/* Right Phone Mockup matching screenshot */}
            <div className="lg:col-span-5 flex justify-center items-center">
              <div className="relative transform rotate-6 hover:rotate-0 transition-transform duration-500 max-w-[280px] w-full">
                
                {/* Phone Body Shell */}
                <div className="relative rounded-[40px] border-[8px] border-slate-900 bg-slate-950 p-2.5 shadow-2xl">
                  {/* Speaker notch */}
                  <div className="w-24 h-4 bg-slate-900 rounded-full mx-auto mb-2" />

                  {/* Phone Screen Mockup Content */}
                  <div className="rounded-[28px] overflow-hidden bg-white text-slate-900 p-3 space-y-3">
                    
                    {/* Mini in-app Header */}
                    <div className="flex items-center justify-between pb-2 border-b border-slate-100">
                      <div className="flex items-center gap-1.5">
                        <PairUpLogo className="w-5 h-5 shrink-0" />
                        <span className="font-extrabold text-xs text-slate-900">PairUpToday</span>
                      </div>
                      <span className="text-[9px] bg-pink-50 text-pink-600 font-bold px-1.5 py-0.5 rounded-full">
                        Live
                      </span>
                    </div>

                    {/* Mini companion card 1 */}
                    <div className="flex items-center gap-2 p-2 rounded-xl bg-pink-50/50 border border-pink-100">
                      <img
                        src="https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=150&q=80"
                        alt="Riya Sharma"
                        className="w-10 h-10 rounded-lg object-cover"
                      />
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <p className="text-[11px] font-bold text-slate-900">Riya Sharma, 24</p>
                          <span className="text-[10px] font-bold text-pink-600">₹999/h</span>
                        </div>
                        <p className="text-[9px] text-slate-500">📍 Kolkata • ★ 4.8</p>
                      </div>
                    </div>

                    {/* Mini companion card 2 */}
                    <div className="flex items-center gap-2 p-2 rounded-xl bg-purple-50/50 border border-purple-100">
                      <img
                        src="https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?auto=format&fit=crop&w=150&q=80"
                        alt="Arjun Verma"
                        className="w-10 h-10 rounded-lg object-cover"
                      />
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <p className="text-[11px] font-bold text-slate-900">Arjun Verma, 26</p>
                          <span className="text-[10px] font-bold text-purple-600">₹1199/h</span>
                        </div>
                        <p className="text-[9px] text-slate-500">📍 Mumbai • ★ 4.9</p>
                      </div>
                    </div>

                    {/* Mini Book CTA button */}
                    <div className="py-1.5 px-3 bg-gradient-to-r from-pink-500 to-rose-500 text-white rounded-xl text-center text-[10px] font-bold shadow-md shadow-pink-500/20">
                      Instant Booking (5 mins)
                    </div>

                  </div>

                </div>

              </div>
            </div>

          </div>

        </div>

      </div>
    </section>
  );
};
